import { resolveBatchList, resolveToolApprovalRenderer } from "#components/ui/tool-approval/renderers/index.ts";
import {
    buildBatchChoices,
    buildResumeEntry,
    resolveBatchIntegrationKeys,
    resolveRowChoices,
    type ResolvedChoice,
    type RowDecision,
    type ToolApprovalChoice
} from "#components/ui/tool-approval/toolApprovalDecision.ts";
import { ToolApprovalBatchList } from "#components/ui/tool-approval/ToolApprovalBatchList.tsx";
import { ToolApprovalRow } from "#components/ui/tool-approval/ToolApprovalRow.tsx";
import { PendingPromptContext } from "#features/conversations/components/conversation-view/PendingPromptContext.ts";
import { DraftActions, DraftCardProvider, DraftFooter } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { useCreateConversationMessage } from "#features/conversations/hooks/useCreateConversationMessage.ts";
import { INTEGRATION_MANIFESTS, type IntegrationManifest } from "#features/conversations/integrations/index.ts";
import { answerPendingApproval } from "#features/conversations/lib/toolApprovalRecords.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { PendingApprovalCall, ToolApprovalEditError, ToolApprovalRecord } from "#types/ToolApproval.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { Button, Div, Flex, Text } from "@hopper-ui/components";
import { AngleDownIcon, AngleUpIcon, SparklesIcon } from "@hopper-ui/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { VisuallyHidden } from "react-aria-components";
import "./ToolApprovalCard.css";
import "./ToolApprovalCard.dark.css";

interface Props {
    conversationId: string;
    approval: ToolApprovalRecord;
}

const NO_EDITS: Record<string, unknown> = {};
const NO_EDIT_ERRORS: ToolApprovalEditError[] = [];
const NOT_A_DRAFT = { mode: "editing" as const };

/** Edits per call, seeded from the pending calls' echoed `editedArgs` (re-presented after a rejection). */
function seedEdits(calls: PendingApprovalCall[]): Record<string, Record<string, unknown>> {
    return Object.fromEntries(calls.flatMap(call => (call.editedArgs ? [[call.toolCallId, call.editedArgs]] : [])));
}

/**
 * The one provider a batch writes through, or null when it writes through several.
 *
 * Naming the first provider found would let a collapsed card claim every hidden row belongs to it,
 * so a mixed batch gets no badge — and the caller keeps it expanded.
 */
function resolveManifest(calls: { args: Record<string, unknown> }[]): IntegrationManifest | null {
    const keys = resolveBatchIntegrationKeys(calls);
    if (keys.length !== 1) {
        return null;
    }
    const [key] = keys;

    return key !== undefined && Object.hasOwn(INTEGRATION_MANIFESTS, key)
        ? INTEGRATION_MANIFESTS[key as keyof typeof INTEGRATION_MANIFESTS]
        : null;
}

// Focus stays in the composer when the card appears, so a live region tells a screen reader it arrived.
// A live region reads changes, not the content it mounts with, so the text lands after mount.
function ArrivalAnnouncement({ message }: { message: string }) {
    const [text, setText] = useState("");
    useEffect(() => {
        setText(message);
    }, [message]);

    return <VisuallyHidden role="status">{text}</VisuallyHidden>;
}

export function ToolApprovalCard({ conversationId, approval }: Props) {
    const { calls: toolCalls } = approval;
    const { createConversationMessage } = useCreateConversationMessage();
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const queryClient = useQueryClient();
    const pendingPromptRef = useContext(PendingPromptContext);
    const containerRef = useRef<HTMLDivElement>(null);
    // Every decision path posts and then hands the call to the answered card. Guard so a double-press cannot post twice.
    const hasSubmittedRef = useRef(false);

    const [decisions, setDecisions] = useState<Record<string, RowDecision>>({});
    // Two row presses in one React batch would both merge against the same render-time
    // `decisions`, dropping the first. The ref always holds the newest set.
    const decisionsRef = useRef<Record<string, RowDecision>>({});
    // Per-row controls stay hidden until asked for. A uniform allow/deny is the common case (6 of 7
    // batches in production), and showing two buttons on every row buries the card-level actions.
    const [isRowMode, setIsRowMode] = useState(false);
    // A batch opens collapsed — eight actions at full height buries the decision buttons.
    const [isExpanded, setIsExpanded] = useState(false);
    // A card that throws gives way to the generic content, so its call also gets the generic prompt (spec R5).
    const [failedCards, setFailedCards] = useState<ReadonlySet<string>>(new Set());
    const markCardFailed = useCallback((toolCallId: string) => {
        setFailedCards(current => (current.has(toolCallId) ? current : new Set([...current, toolCallId])));
    }, []);

    const isBatch = toolCalls.length > 1;

    // Edits per call, seeded from the echoed editedArgs when core re-presents a rejected edit. Held in a
    // ref too so a decision posted in the same React batch as an edit reads the newest value.
    const [edits, setEdits] = useState(() => seedEdits(toolCalls));
    const editsRef = useRef(edits);
    useEffect(() => {
        const seeded = seedEdits(toolCalls);
        editsRef.current = seeded;
        setEdits(seeded);
    }, [toolCalls]);

    const editCall = useCallback((toolCallId: string, partial: Record<string, unknown>) => {
        const next = { ...editsRef.current, [toolCallId]: { ...editsRef.current[toolCallId], ...partial } };
        editsRef.current = next;
        setEdits(next);
    }, []);

    const decidableCalls = useCallback(
        () => toolCalls.map(call => ({ toolCallId: call.toolCallId, interruptId: call.interruptId, editedArgs: editsRef.current[call.toolCallId] })),
        [toolCalls]
    );

    // Land focus on the prompt itself, never on a decision button — an errant Enter must not
    // approve a write the user has not read. Leave focus alone when something holds it, like the composer.
    useEffect(() => {
        if (!document.activeElement || document.activeElement === document.body) {
            containerRef.current?.focus({ preventScroll: true });
        }
    }, []);

    // The conversation scrolls to the prompt when a turn ends on it; a layout effect, so the element is in place before it looks.
    useLayoutEffect(() => {
        if (pendingPromptRef) {
            pendingPromptRef.current = containerRef.current;
        }

        return () => {
            if (pendingPromptRef) {
                pendingPromptRef.current = null;
            }
        };
    }, [pendingPromptRef]);

    const submit = useCallback((choices: ResolvedChoice[]) => {
        if (hasSubmittedRef.current) {
            return;
        }
        hasSubmittedRef.current = true;

        // Every pending call carries its interrupt id: the live interrupt sets it, and a replayed record has it from core.
        const resume = choices.map(({ interruptId, choice, editedArgs }) => buildResumeEntry({ interruptId: interruptId!, choice, editedArgs }));
        createConversationMessage(conversationId, "", {
            resume,
            onError: () => {
                // The decision may have already resolved server-side despite this error, so refetch
                // rather than restore this now-possibly-stale snapshot.
                hasSubmittedRef.current = false;
                queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetConversation(conversationId) });
            }
        });
        // The prompt gives way to the answered card, which shows what was approved, edits included.
        const choiceByCall = new Map(choices.map(choice => [choice.toolCallId, choice]));
        answerPendingApproval(queryClient, conversationId, approval, ({ toolCallId }) => {
            const { choice, editedArgs } = choiceByCall.get(toolCallId) ?? { choice: "deny" };

            return choice === "deny" ? { decision: "deny" } : { decision: "allow", editedArgs };
        });
        decisionsRef.current = {};
        setDecisions({});
        editsRef.current = {};
        setEdits({});
    }, [conversationId, approval, createConversationMessage, queryClient]);

    /** Apply one choice to every pending call and post immediately — the whole-card actions. */
    const decideAll = useCallback((choice: ToolApprovalChoice) => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Tool Approval Card",
            Trigger: `Decided All: ${choice}`,
            "Call Count": toolCalls.length
        });
        submit(buildBatchChoices(decidableCalls(), choice));
    }, [toolCalls, decidableCalls, submit, trackEvent]);

    /**
     * Record decisions for some calls. A resume must resolve every pending call, so the post waits for the
     * last one — then fires without a separate confirm step.
     */
    const applyDecisions = useCallback((next: Record<string, RowDecision>, trigger: string) => {
        decisionsRef.current = next;
        setDecisions(next);
        const resolved = resolveRowChoices(decidableCalls(), next);
        if (resolved !== null) {
            trackEvent(MixpanelEvent.ButtonClicked, {
                Location: "Tool Approval Card",
                Trigger: trigger,
                "Call Count": toolCalls.length
            });
            submit(resolved);
        }
    }, [toolCalls, decidableCalls, submit, trackEvent]);

    const decideRow = useCallback((toolCallId: string, decision: RowDecision) => {
        applyDecisions({ ...decisionsRef.current, [toolCallId]: decision }, "Decided Per Row");
    }, [applyDecisions]);

    if (toolCalls.length === 0) {
        return null;
    }

    const manifest = resolveManifest(toolCalls);
    // Several providers in one batch: never hide rows behind a badge that cannot describe them.
    const isMixed = resolveBatchIntegrationKeys(toolCalls).length > 1;
    // A bespoke card is the point of the prompt, so a batch holding one is never hidden behind the summary.
    const hasBespoke = toolCalls.some(call => resolveToolApprovalRenderer(call) !== null);
    const batchList = resolveBatchList(toolCalls);
    const canCollapse = isBatch && !isMixed && !hasBespoke;
    const provenance = [
        manifest?.displayName ?? tShared(isMixed ? "toolApproval.mixedProviders" : "toolApproval.workleapAction"),
        isBatch ? tShared("toolApproval.actionCount", { count: toolCalls.length }) : null
    ].filter(Boolean).join(" · ");
    const summaryLabel = `${tShared("toolApproval.promptLabel")} · ${provenance}`;
    // A lone call whose card names its decisions shows the card's own words and no header or "stop asking" choice.
    const decisionLabels = isBatch || failedCards.has(toolCalls[0]!.toolCallId) ? undefined : resolveToolApprovalRenderer(toolCalls[0]!)?.decisionLabels;
    // That card is the whole prompt: its own frame, with Send and Discard in its footer.
    const draftCard = decisionLabels && {
        mode: "editing" as const,
        footer: (
            <DraftFooter
                actions={(
                    <DraftActions
                        sendLabel={tShared(decisionLabels.allow)}
                        discardLabel={tShared(decisionLabels.deny)}
                        onSend={() => decideAll("allow")}
                        onDiscard={() => decideAll("deny")}
                    />
                )}
            />
        )
    };

    const isDraft = draftCard || batchList !== null;

    return (
        <Flex
            ref={containerRef}
            tabIndex={-1}
            role="group"
            aria-label={summaryLabel}
            className={isDraft ? "wai-tool-approval-card wai-tool-approval-card--framed" : "wai-tool-approval-card"}
            direction="column"
            position="relative"
            overflow={isDraft ? undefined : "hidden"}
            borderRadius={isDraft ? undefined : "rounded-lg"}
            padding={isDraft ? undefined : "inset-md"}
            UNSAFE_maxHeight={isDraft ? undefined : "min(30rem, 70vh)"}
        >
            <ArrivalAnnouncement message={summaryLabel} />
            {/* Provenance, not a generic "approval required" banner: name the provider being written
                to. A Workleap-native write names none, so the row carries only the batch count. */}
            {!isDraft && <Flex alignItems="center" marginBottom="inline-sm" shrink={0}>
                {/* Native button: Hopper's Button routes icon children into its own leading slot,
                    which splits the glyph away from the label it belongs to. */}
                <button
                    type="button"
                    className="wai-tool-approval-summary"
                    disabled={!canCollapse}
                    aria-expanded={canCollapse ? isExpanded : undefined}
                    onClick={() => setIsExpanded(current => !current)}
                >
                    {manifest !== null ? <manifest.glyph /> : <SparklesIcon size="sm" />}
                    <Text size="xs" fontWeight="body-xs-medium" color="neutral-weak">
                        {provenance}
                    </Text>
                    {canCollapse && (isExpanded ? <AngleUpIcon size="sm" /> : <AngleDownIcon size="sm" />)}
                </button>
            </Flex>}

            {batchList && (
                <ToolApprovalBatchList
                    calls={toolCalls}
                    batch={batchList}
                    edits={edits}
                    onEditArgs={editCall}
                    onBookAll={() => decideAll("allow")}
                    onDiscard={() => decideAll("deny")}
                />
            )}

            {!batchList && (!canCollapse || isExpanded) && (
                <Div flexShrink={1} minHeight={0} overflow={draftCard ? undefined : "hidden auto"} marginBottom={draftCard ? undefined : "inline-md"}>
                    <DraftCardProvider value={draftCard || NOT_A_DRAFT}>
                        <Flex direction="column">
                            {toolCalls.map(call => (
                                <ToolApprovalRow
                                    key={call.toolCallId}
                                    call={call}
                                    isBatch={isBatch}
                                    showRowChoices={isBatch && isRowMode}
                                    decision={decisions[call.toolCallId]}
                                    onDecideRow={decideRow}
                                    edits={edits[call.toolCallId] ?? NO_EDITS}
                                    onEditArgs={editCall}
                                    editErrors={call.editErrors ?? NO_EDIT_ERRORS}
                                    onCardError={markCardFailed}
                                />
                            ))}
                        </Flex>
                    </DraftCardProvider>
                </Div>
            )}

            {/* Two balanced actions, then the wider grant as a quiet one-press link beneath them.
                As a third peer button it competed with allow/deny and its label does not survive
                translation at button width. Deny stays neutral — the risk is in allowing. */}
            {!isDraft && <Flex direction="column" gap="inline-xs" shrink={0}>
                <Flex gap="inline-sm" alignItems="center">
                    <Button variant="primary" size="sm" borderRadius="rounded-md" onPress={() => decideAll("allow")}>
                        {isBatch ? tShared("toolApproval.allowAll") : tShared("toolApproval.allow")}
                    </Button>
                    <Button variant="ghost-secondary" size="sm" borderRadius="rounded-md" onPress={() => decideAll("deny")}>
                        {isBatch ? tShared("toolApproval.denyAll") : tShared("toolApproval.deny")}
                    </Button>
                </Flex>
                <Flex gap="inline-md" alignItems="center" wrap="wrap">
                    <Button variant="ghost-secondary" size="sm" onPress={() => decideAll("alwaysAllow")}>
                        <Text size="xs" color="neutral-weak">{tShared("toolApproval.allowAndRemember")}</Text>
                    </Button>
                    {isBatch && !isRowMode && (
                        <Button variant="ghost-secondary" size="sm" onPress={() => { setIsRowMode(true); setIsExpanded(true); }}>
                            <Text size="xs" color="neutral-weak">{tShared("toolApproval.decideEach")}</Text>
                        </Button>
                    )}
                </Flex>
            </Flex>}
        </Flex>
    );
}
